import { ActionIcon, Box, Center, Flex, Group, Loader, Text, Transition } from '@mantine/core';
import { IconPencilPlus } from '@tabler/icons-react';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { MaskedSidebarVideo } from 'src/UI/common/MaskedSidebarVideo';
import { SiestaBackground } from 'src/UI/common/SiestaBackground';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useAppContainerContext } from '../AppContainerContext';
import { ChatsView } from '../chats/ChatsView';
import { useChatScroll } from '../chat/hooks/useChatScroll';
import { isChatMessage } from '../chat/types';
import { useSiesta } from '../SiestaProvider';
import { SteeringActionContent } from '../steering/SteeringActionContent';
import { SiestaAction, SiestaActionType } from '../types';
import { useAppContainer } from '../useAppContainer';
import { ActionModeScrollArea } from './ActionModeScrollArea';
import { AIActionSet } from './AIActionSet';
import { AskSiestaInput } from './AskSiestaInput';
import { ChatEmptyState } from './ChatEmptyState';
import { ChatScrollBlur } from './ChatScrollBlur';
import { CtaAdditionalAI } from './CtaAdditionalAI';
import { DisplacedChatBanner } from './DisplacedChatBanner';
import classes from './SiestaBar.module.css';
import { SiestaBusyGlow } from './SiestaBusyGlow';
import { ChatModeTabs, type ChatBarTab } from './ChatModeTabs';
import { SiestaBarMessages } from './SiestaBarMessages';
import { SiestaChatInput } from './SiestaChatInput';
import { useChatEmptyMessage } from './useChatEmptyMessage';
import { useRecordEditAction } from './useRecordEditAction';
import { useSuggestedActions } from './useSuggestedActions';

export function SiestaBar() {
  const { siestaMode, setSiestaMode, siestaBusyGlowTop } = useAppContainer();
  const { contentMeta, conversationId } = useAppContainerContext();
  const emptyMessage = useChatEmptyMessage();
  const { clientId } = useCurrentClientIfAvailable();
  const { recordAction } = useRecordEditAction();
  const { flagEnabledSiestaChats, flagEnabledSiestaSteering } = useFlags();

  const {
    messages,
    latestIncomingId,
    currentChatState,
    sendUserMessage,
    discardChat,
    resetIfDisplaced,
    previousChatContext,
    goBack,
    markActionUsed,
    usedActionIds,
    finaliseLimitReachedMessage,
  } = useSiesta();

  const isProcessing = currentChatState !== null;
  const segmentValue: ChatBarTab = siestaMode === 'busy' ? 'action' : siestaMode;
  const compactSteering = flagEnabledSiestaChats && !flagEnabledSiestaSteering;

  const lastMessage = messages[messages.length - 1];
  const contentSignal =
    lastMessage && isChatMessage(lastMessage) ? lastMessage.answerContent.length : 0;
  const { scrollRef, handleScroll, forceScrollToBottom } = useChatScroll(
    messages.length,
    contentSignal,
  );

  const { actions: siestaActions, isLoading: isLoadingActions } = useSuggestedActions({
    documentId: contentMeta?.entityId,
    documentContent: contentMeta?.metadata?.documentContent,
    clientId,
    conversationId,
  });

  const visibleActions = siestaActions.filter(a => !usedActionIds.has(a.id));

  const handleActionClick = async (action: SiestaAction) => {
    if (action.type !== SiestaActionType.CHAT) return;

    const activeConversationId = resetIfDisplaced();
    markActionUsed(action.id);
    setSiestaMode('chat');

    await sendUserMessage(action.prompt ?? action.label, clientId ?? null, activeConversationId);
    forceScrollToBottom();

    if (contentMeta?.entityId && clientId) {
      recordAction({
        documentId: contentMeta.entityId,
        actionText: action.label,
        source: 'SUGGESTED',
        conversationId: activeConversationId,
        clientId,
      });
    }
  };

  const handleInputSubmit = async (message: string) => {
    const activeConversationId = resetIfDisplaced();
    setSiestaMode('chat');
    await sendUserMessage(message, clientId ?? null, activeConversationId);
    forceScrollToBottom();
  };

  const handleContinue = async (widgetMessageId: string) => {
    const activeConversationId = resetIfDisplaced();
    setSiestaMode('chat');
    finaliseLimitReachedMessage(widgetMessageId);
    await sendUserMessage('Continue', clientId ?? null, activeConversationId);
    forceScrollToBottom();
  };

  const handleInputFocus = () => {
    if (siestaMode === 'action') {
      setSiestaMode('chat');
    }
  };

  const handleBackToActions = () => {
    discardChat();
    setSiestaMode('action');
  };

  const handleNewChat = () => {
    discardChat();
    setSiestaMode('chat');
  };

  const showOrangeBackground = !flagEnabledSiestaChats;
  const isSteering = siestaMode === 'action';
  const isEmptyChat = siestaMode === 'chat' && messages.length === 0;
  const flameHeight = resolveFlameHeight(isSteering, isEmptyChat);
  const flameOpacity = isSteering || isEmptyChat ? 0.75 : 0.3;

  return (
    <Box pos="relative" h="100%" style={{ overflow: 'hidden', borderRadius: 'inherit' }}>
      {showOrangeBackground ? (
        <SiestaBackground />
      ) : (
        <>
          <Box className={classes.chatSurface} />
          <Box className={classes.chatFlames} style={{ height: flameHeight }}>
            <MaskedSidebarVideo opacity={flameOpacity} scaleX={4} />
          </Box>
        </>
      )}

      <Flex direction="column" h="100%" pos="relative" style={{ zIndex: 2 }}>
        {flagEnabledSiestaChats && siestaMode !== 'busy' && (
          <Box px="xl" pt="md">
            <ChatModeTabs value={segmentValue} onChange={setSiestaMode} />
          </Box>
        )}

        <Box pos="relative" flex={1} mih={0}>
          <Transition mounted={siestaMode === 'busy'} transition="fade" duration={200} keepMounted>
            {styles => (
              <Box
                className={classes.busyContainer}
                px="xxxl"
                pos="absolute"
                top="xl"
                style={{ ...styles, overflow: 'hidden', inset: 0 }}
              >
                <SiestaBusyGlow top={siestaBusyGlowTop ?? '50%'} />
                <Text
                  size="textXl"
                  fw={FONT_WEIGHTS.semibold}
                  c="#333"
                  className={classes.busyText}
                >
                  {'Siesta is busy,\nfinish the action to turn it back on'}
                </Text>
              </Box>
            )}
          </Transition>

          <Transition mounted={siestaMode === 'chat'} transition="fade" duration={200}>
            {styles => (
              <Flex
                bg={flagEnabledSiestaChats ? undefined : 'white'}
                direction="column"
                h="100%"
                pos="absolute"
                px="xl"
                pb={flagEnabledSiestaChats ? 'md' : 'xxxl'}
                style={{ ...styles, inset: 0 }}
              >
                {flagEnabledSiestaChats ? (
                  <Group justify="flex-end">
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="lg"
                      aria-label="Start a new chat"
                      onClick={handleNewChat}
                      disabled={isProcessing}
                    >
                      <IconPencilPlus size={22} />
                    </ActionIcon>
                  </Group>
                ) : (
                  <CtaAdditionalAI onClick={handleBackToActions} disabled={isProcessing} />
                )}
                <ChatScrollBlur dark={flagEnabledSiestaChats} />

                <Box
                  ref={scrollRef}
                  onScroll={handleScroll}
                  flex={1}
                  pt="xxxxl"
                  style={{ overflow: 'auto' }}
                >
                  {messages.length === 0 ? (
                    <ChatEmptyState
                      title={emptyMessage.title}
                      subtitle={emptyMessage.subtitle}
                      dark={flagEnabledSiestaChats}
                    />
                  ) : (
                    <SiestaBarMessages
                      messages={messages}
                      latestIncomingId={latestIncomingId ?? undefined}
                      isStreaming={isProcessing}
                      onContinue={handleContinue}
                      dark={flagEnabledSiestaChats}
                    />
                  )}
                </Box>

                <DisplacedChatBanner previousChatContext={previousChatContext} onGoBack={goBack} />
                {!flagEnabledSiestaChats && (
                  <AskSiestaInput
                    variant="brand"
                    onSubmit={handleInputSubmit}
                    autoFocus
                    submitDisabled={isProcessing}
                  />
                )}
              </Flex>
            )}
          </Transition>

          <Transition
            mounted={siestaMode === 'action'}
            transition="fade"
            duration={200}
            keepMounted
          >
            {styles => (
              <Box
                pos="absolute"
                px="xl"
                pt={compactSteering ? 'md' : 'xxxl'}
                pb="xxxl"
                style={{ ...styles, inset: 0 }}
              >
                <Flex h="100%" direction="column">
                  <ActionModeScrollArea>
                    <ActionModeContent
                      isLoadingActions={isLoadingActions}
                      visibleActions={visibleActions}
                      onActionClick={handleActionClick}
                      compact={compactSteering}
                    />
                  </ActionModeScrollArea>
                  {!flagEnabledSiestaChats && (
                    <AskSiestaInput
                      variant="light"
                      onSubmit={handleInputSubmit}
                      onFocus={handleInputFocus}
                    />
                  )}
                </Flex>
              </Box>
            )}
          </Transition>

          <Transition mounted={siestaMode === 'history'} transition="fade" duration={200}>
            {styles => (
              <Box pos="absolute" px="xl" pt="md" pb="md" style={{ ...styles, inset: 0 }}>
                <ChatsView onNewChat={handleNewChat} />
              </Box>
            )}
          </Transition>
        </Box>

        {flagEnabledSiestaChats && siestaMode !== 'busy' && (
          <Box px="xl" pt="md" pb="xxxl">
            <SiestaChatInput
              clientId={clientId ?? null}
              conversationId={conversationId}
              onSubmit={handleInputSubmit}
              onFocus={handleInputFocus}
              submitDisabled={isProcessing}
            />
          </Box>
        )}
      </Flex>
    </Box>
  );
}

function resolveFlameHeight(isSteering: boolean, isEmptyChat: boolean): string {
  if (isSteering) return '200%';
  if (isEmptyChat) return '100%';
  return '50%';
}

function ActionModeContent({
  isLoadingActions,
  visibleActions,
  onActionClick,
  compact,
}: {
  isLoadingActions: boolean;
  visibleActions: SiestaAction[];
  onActionClick: (action: SiestaAction) => void;
  compact: boolean;
}) {
  const { flagEnabledSiestaSteering } = useFlags();

  if (flagEnabledSiestaSteering) {
    return (
      <Flex flex={1} direction="column" style={{ minHeight: 0 }}>
        <SteeringActionContent suggestedActions={visibleActions} onActionClick={onActionClick} />
      </Flex>
    );
  }

  if (isLoadingActions) {
    return (
      <Center flex={1}>
        <Loader color="white" />;
      </Center>
    );
  }

  return (
    <Flex flex={1} align={compact ? 'flex-start' : 'center'} justify="center">
      <AIActionSet actions={visibleActions} onActionClick={onActionClick} compact={compact} />
    </Flex>
  );
}
